import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  PasswordInput,
  Stack,
  Table,
  Text,
  Textarea,
} from '@mantine/core'
import { IconAlertCircle, IconTrash, IconUserPlus } from '@tabler/icons-react'
import { useState } from 'react'
import {
  addAllowedUser,
  listAllowedUsers,
  removeAllowedUser,
  type AllowlistUser,
} from '../../api/allowlist'

import { parseEmails } from './parseEmails'

interface AdminUsersProps {
  opened: boolean
  onClose: () => void
}

// Admin panel for managing the SSO allowlist from the UI. The admin token is
// held in local state only (never persisted) and passed through to the
// protected /api/allowlist endpoints on each request.
export function AdminUsers({ opened, onClose }: AdminUsersProps) {
  const [token, setToken] = useState('')
  const [connected, setConnected] = useState(false)
  const [users, setUsers] = useState<AllowlistUser[]>([])
  const [newEmails, setNewEmails] = useState('')
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadUsers = async () => {
    if (!token.trim()) {
      setError('Enter the admin token first.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const list = await listAllowedUsers(token)
      setUsers(list)
      setConnected(true)
    } catch (e) {
      setConnected(false)
      setError(e instanceof Error ? e.message : 'Failed to load users.')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = async () => {
    const emails = parseEmails(newEmails)
    if (emails.length === 0) {
      setError('Enter at least one email address to add.')
      return
    }

    // De-duplicate within the input itself
    const unique = [...new Set(emails)]

    // Check against the already-loaded allowlist
    const existing = new Set(users.map((u) => u.email.toLowerCase()))
    const duplicates = unique.filter((e) => existing.has(e))
    const toAdd = unique.filter((e) => !existing.has(e))

    if (toAdd.length === 0) {
      const label = duplicates.length === 1 ? 'is' : 'are'
      setError(
        `${duplicates.join(', ')} ${label} already on the allowlist.`
      )
      return
    }

    setBusy(true)
    setError(null)
    try {
      await Promise.all(toAdd.map((email) => addAllowedUser(token, email)))
      setNewEmails('')
      const list = await listAllowedUsers(token)
      setUsers(list)
      if (duplicates.length > 0) {
        setError(
          `Added ${toAdd.length} user${toAdd.length === 1 ? '' : 's'}. Skipped ${duplicates.length} already on the allowlist: ${duplicates.join(', ')}`
        )
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add user(s).')
    } finally {
      setBusy(false)
    }
  }

  const handleRemove = async (email: string) => {
    setBusy(true)
    setError(null)
    try {
      await removeAllowedUser(token, email)
      setUsers((prev) => prev.filter((u) => u.email !== email))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove user.')
    } finally {
      setBusy(false)
    }
  }

  const handleClose = () => {
    // Drop the token and any loaded state when the panel is dismissed.
    setToken('')
    setConnected(false)
    setUsers([])
    setNewEmails('')
    setError(null)
    onClose()
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Manage SSO users"
      size="lg"
      centered
    >
      <Stack gap="md">
        {error && (
          <Alert
            color="red"
            icon={<IconAlertCircle size={16} />}
            title="Error"
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Group align="flex-end" gap="sm">
          <PasswordInput
            label="Admin token"
            description="Required to view and modify the allowlist."
            placeholder="PRISM_ADMIN_TOKEN"
            value={token}
            onChange={(e) => setToken(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') loadUsers()
            }}
            style={{ flex: 1 }}
          />
          <Button onClick={loadUsers} loading={loading}>
            {connected ? 'Refresh' : 'Connect'}
          </Button>
        </Group>

        {connected && (
          <>
            <Stack gap="xs">
              <Textarea
                label="Add users"
                description="Enter one or more emails separated by commas, semicolons, or newlines."
                placeholder={"user1@loandepot.com, user2@loandepot.com"}
                value={newEmails}
                onChange={(e) => setNewEmails(e.currentTarget.value)}
                autosize
                minRows={1}
                maxRows={6}
              />
              <Group justify="flex-end">
                <Button
                  leftSection={<IconUserPlus size={16} />}
                  onClick={handleAdd}
                  loading={busy}
                >
                  Add
                </Button>
              </Group>
            </Stack>

            <Text size="sm" c="dimmed">
              {users.length} user{users.length === 1 ? '' : 's'} on the
              allowlist
              {users.length === 0
                ? ' — an empty allowlist means everyone who can sign in is allowed.'
                : '.'}
            </Text>

            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Added by</Table.Th>
                  <Table.Th>Added at</Table.Th>
                  <Table.Th w={48} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {users.map((user) => (
                  <Table.Tr key={user.email}>
                    <Table.Td>{user.email}</Table.Td>
                    <Table.Td>{user.added_by ?? '—'}</Table.Td>
                    <Table.Td>{user.added_at ?? '—'}</Table.Td>
                    <Table.Td>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label={`Remove ${user.email}`}
                        onClick={() => handleRemove(user.email)}
                        disabled={busy}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </>
        )}

        {loading && !connected && (
          <Group justify="center">
            <Loader size="sm" />
          </Group>
        )}
      </Stack>
    </Modal>
  )
}
